/**
 * useHoldToConfirm — press-and-hold to trigger an action, with progress.
 *
 * For destructive or hard-to-undo actions on touch, where a tap is too easy to do
 * by accident and there is no hover state to warn you first.
 *
 * Returns `{ handlers, holding, progressPct }`. Spread `handlers` onto the element
 * and paint `progressPct` however suits it.
 *
 * Three details are deliberate:
 *
 *  - **A ref holds the gesture state, not React state.** The rAF loop reads it every
 *    frame; state would give the loop a stale snapshot and let a completed hold fire
 *    twice.
 *  - **`onPointerDown` stops propagation.** These buttons sit inside cards that own
 *    their own hold gesture, so without it one press starts both.
 *  - **Cancel on pointerup, pointerleave and pointercancel.** Missing
 *    `pointercancel` is what leaves a stuck progress bar when the browser takes the
 *    pointer for a scroll.
 *
 * @param {object} args
 * @param {boolean} args.enabled — whether a hold may start at all
 * @param {() => void} args.onComplete — fired once when the hold completes
 * @param {number} [args.durationMs]
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_HOLD_MS = 1500;

export default function useHoldToConfirm({ enabled, onComplete, durationMs = DEFAULT_HOLD_MS }) {
    const [holding, setHolding] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const state = useRef({ active: false, fired: false, startedAt: 0 });
    const raf = useRef(0);
    /* Held in a ref so the rAF loop always calls the latest callback without the
       loop itself having to be torn down and restarted on every render. */
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const stop = useCallback(() => {
        state.current.active = false;
        state.current.fired = false;
        if (raf.current) {
            cancelAnimationFrame(raf.current);
            raf.current = 0;
        }
        setHolding(false);
        setElapsed(0);
    }, []);

    const step = useCallback(() => {
        if (!state.current.active) {
            raf.current = 0;
            return;
        }
        const delta = Date.now() - state.current.startedAt;
        setElapsed(delta);
        if (delta >= durationMs && !state.current.fired) {
            state.current.fired = true;
            state.current.active = false;
            raf.current = 0;
            setHolding(false);
            setElapsed(0);
            onCompleteRef.current?.();
            return;
        }
        raf.current = requestAnimationFrame(step);
    }, [durationMs]);

    const start = useCallback((e) => {
        e?.stopPropagation?.();
        if (!enabled) return;
        if (raf.current) cancelAnimationFrame(raf.current);
        state.current = { active: true, fired: false, startedAt: Date.now() };
        setElapsed(0);
        setHolding(true);
        raf.current = requestAnimationFrame(step);
    }, [enabled, step]);

    useEffect(() => stop, [stop]);

    /* A hold that is no longer allowed must not keep animating. */
    useEffect(() => {
        if (!enabled) stop();
    }, [enabled, stop]);

    return {
        holding,
        progressPct: holding ? Math.min(100, (elapsed / durationMs) * 100) : 0,
        handlers: {
            onPointerDown: start,
            onPointerUp: stop,
            onPointerLeave: stop,
            onPointerCancel: stop,
            /* The card body below listens for clicks to toggle its expanded state;
               a press on this button must not reach it. */
            onClick: (e) => e.stopPropagation(),
        },
    };
}
